import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getPersonSuggestions } from "../utils/api";

/** @typedef {import('../utils/types/api').SuggestionPerson} Employee */
/** @typedef {import("../utils/types/api").GetPersonSuggestionResponse} GetPersonSuggestionResponse */

/**
 * 
 * @returns {string}
 */
const getSearchHistoryKey = () => {
  const path = window.location.pathname;
  const lastWord = path.split("/").filter(Boolean).pop() || "default";

  return `employee-search-history-${lastWord}`;
};

const MAX_HISTORY = 5;
const DEBOUNCE_DELAY = 350;

const HighlightText = React.memo(({ text, query }) => {
  if (!query) {
    return <>{text}</>;
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");

  return (
    <>
      {text.split(regex).map((part, index) => {
        const isMatch = part.toLowerCase() === query.toLowerCase();

        return isMatch ? (
          <Box
            component="span"
            key={index}
            sx={{
              backgroundColor: "#2526290a", // similar to your sidebar
              color: "#455A91",
              fontWeight: 700,
              borderRadius: "2px",
              px: 0.1,
              py: 0.1,
            }}
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        );
      })}
    </>
  );
});

/**
 * @typedef {Object} PersonSearchProps
 * @property {(person: Employee) => void} [onSelect]
 * @property {(value: string) => void} [onQueryChange] Called when the user
 *   types or clears the query.
 * @property {Object} [queryParams]
 * @property {number} [resetKey] Changes to this value clear the search input.
 * @property {string} [placeholder] Placeholder text for the search input.
 */
/**
 * PersonSearch
 * -------------
 *
 * @description
 * ```
 * - Debounced search suggestion
 * - recent search history
 * ```
 *
 * @type {import("react").FunctionComponent<PersonSearchProps>}
 **/
export const PersonSearch = React.memo(({ onSelect, onQueryChange, queryParams, resetKey, placeholder }) => {

  queryParams = { ...queryParams, limit: 5 }; // Always limit to 5 results

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  /** @type {[Employee[], React.Dispatch<React.SetStateAction<Employee[]>>]} */
  const [history, setHistory] = useState([]);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  /** @type {[GetPersonSuggestionResponse, React.Dispatch<React.SetStateAction<GetPersonSuggestionResponse>>]} */
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const skipNextSearchRef = useRef(false);

  const searchHistoryKey = getSearchHistoryKey();
const searchPlaceholder = useMemo(() => {
  return placeholder || "Search by name, person ID or employee code";
}, [placeholder]);

  // Load Search history
  useEffect(() => {
    try {
      const saved = localStorage.getItem(searchHistoryKey);

      if (saved) {
        setHistory(JSON.parse(saved));
      }
    } catch {
      setHistory([]);
    }
  }, [searchHistoryKey]);

  useEffect(() => {
    setQuery("");
    setOpen(false);
  }, [resetKey]);

  /**
   * closing model using `onBlur` idea faild.
   * [issue] : onSelect of list item not working
   *
   * this apporach closing `result model` via handle click outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ------------------------------------------
  // Debounce query
  // ------------------------------------------

  useEffect(() => {

    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      setDebouncedQuery("");
      return;
    }

    if (query) {
      setLoading(true);
    } else {
      setLoading(false);
    }

    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  // ------------------------------------------
  // Fetch suggestions
  // ------------------------------------------

  // Always keep the latest queryParams available to the fetch without making
  // them a dependency — the API should only fire when the user types.
  const queryParamsRef = useRef(queryParams);
  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  useEffect(() => {
    if (!debouncedQuery) {
      setSuggestions({persons: [], projection_fields: []});
      setLoading(false);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);

        const result = await getPersonSuggestions(debouncedQuery, queryParamsRef.current);

        setSuggestions(result);
      } catch (error) {
        setSuggestions({persons: [], projection_fields: []});
        setSnackbar({
          open: true,
          message: error?.message,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
    // Only re-fetch on typing (debounced). queryParams are read from a ref so
    // changing filters does not trigger a new request.
  }, [debouncedQuery]);

  /**
   * @param {Employee} searchValue
   */
  const saveToHistory = (searchValue) => {
    if (!searchValue) return;

    const newHistory = [
      searchValue,
      ...history.filter(
        (item) =>
          item.person_id?.toLowerCase() !== searchValue.person_id?.toLowerCase()
      ),
    ].slice(0, MAX_HISTORY);

    setHistory(newHistory);
    localStorage.setItem(searchHistoryKey, JSON.stringify(newHistory));
  };

  /** @type {Array<Employee>} */
  const filteredResults = useMemo(() => {
    const search = debouncedQuery.trim().toLowerCase();

    if (!search) {
      return [];
    }

    return suggestions?.persons?.filter((employee) => {
      return (
        employee.name?.toLowerCase().includes(search) ||
        employee.employee_code?.toLowerCase().includes(search) ||
        employee.person_id?.toLowerCase().includes(search)
      );
    });
  }, [debouncedQuery, suggestions]);

  /** @type {(employee: Employee) => void} */
  const handleSelect = (employee) => {
    saveToHistory(employee);
    setQuery(employee.name);
    skipNextSearchRef.current = true;
    onSelect?.(employee);
  };

  /** @type {(employee: Employee) => void} */
  const handleHistorySelect = (employee) => {
    setQuery(employee.name);
    skipNextSearchRef.current = true;
    onSelect?.(employee);
  };

  const handleClear = () => {
    setQuery("");
    setOpen(true);
    onQueryChange?.("");

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const showHistory = open && query.trim() === "";
  const showResults = open && query.trim() !== "";

  return (
    <Box
      ref={searchRef}
      sx={{
        width: "100%",
        maxWidth: 260,
        position: "relative",
      }}
    >
      <Tooltip
  title={searchPlaceholder}
  placement="top"
  arrow
  enterDelay={300}
  slotProps={{
    tooltip: {
      sx: {
        fontSize: "0.8rem",
        maxWidth: 350,
        whiteSpace: "normal",
        p: 1,
      },
    },
  }}
>
      <TextField
        fullWidth
        inputRef={inputRef}
        value={query}
        placeholder={searchPlaceholder}
        onChange={(event) => {
          const nextValue = event.target.value;
          setQuery(nextValue);
          setOpen(true);
          // Any typing means the user is doing a name search, so let the parent
          // drop a previously selected `person_id` filter (including when the
          // field is emptied).
          onQueryChange?.(nextValue);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
          }

          if (event.key === "Enter" && query.trim()) {
            if (filteredResults.length > 0) {
              handleSelect(filteredResults[0]);
            }
          }
        }}
        InputProps={{
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                aria-label="Clear search"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 40,
            fontSize: "0.875rem",
          },

          "& .MuiOutlinedInput-input": {
            padding: "7px 8px",
            textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
          },

          "& .MuiInputAdornment-root": {
            margin: 0,
          },
        }}
      /></Tooltip>

      {open && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            top: "calc(100% + 2px)",
            backgroundColor: "#fafafa",
            left: 0,
            right: 0,
            zIndex: 1300,
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          {/* ============================= */}
          {/* SEARCH HISTORY */}
          {/* ============================= */}

          {showHistory && (
            <>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Recent searches
                </Typography>

                {history.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Last {Math.min(history.length, 5)}
                  </Typography>
                )}
              </Box>

              <Divider />

              {history.length === 0 ? (
                <Box
                  sx={{
                    px: 2,
                    py: 3,
                    textAlign: "center",
                  }}
                >
                  <HistoryIcon
                    sx={{
                      color: "text.disabled",
                      fontSize: 32,
                      mb: 0.5,
                    }}
                  />

                  <Typography variant="body2" color="text.secondary">
                    No recent searches
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {history.slice(0, 5).map((employee, index) => (
                    <ListItemButton
                      key={`${employee.person_id || employee.idx || employee.name}-${index}`}
                      onClick={() => handleHistorySelect(employee)}
                      sx={{
                        px: 2,
                        py: 1,
                      }}
                    >
                      <HistoryIcon
                        fontSize="small"
                        sx={{
                          mr: 1.5,
                          color: "text.disabled",
                        }}
                      />

                      <ListItemText
                        primary={employee.name}
                        primaryTypographyProps={{
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </>
          )}

          {/* ============================= */}
          {/* SEARCH RESULTS */}
          {/* ============================= */}

          {showResults && filteredResults.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Search results
                </Typography>
              </Box>

              <Divider />

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 3,
                  }}
                >
                  Searcing.....
                </Box>
              ) : suggestions?.persons?.length === 0 && debouncedQuery ? (
                <Box
                  sx={{
                    px: 2,
                    py: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No employees found for "{query}"
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredResults.map((employee) => (
                    <ListItemButton
                      key={employee.idx}
                      onClick={() => handleSelect(employee)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        {/* Name + Person ID */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          {/* Employee Name */}
                          {suggestions.projection_fields.includes("name") &&
                            employee.name && (
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  minWidth: 0,
                                  //   flex: 1,
                                }}
                              >
                                <HighlightText
                                  text={employee.name}
                                  query={query}
                                />
                              </Typography>
                            )}

                          {/* Person ID */}
                          {suggestions.projection_fields.includes(
                            "person_id",
                          ) &&
                            employee.person_id && (
                              <Chip
                                size="small"
                                label={
                                  <HighlightText
                                    text={employee.person_id}
                                    query={query}
                                  />
                                }
                                sx={{
                                  flexShrink: 0,
                                  height: 24,
                                  borderRadius: 1,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  backgroundColor: "grey.100",
                                  border: "1px solid",
                                  borderColor: "grey.300",

                                  "& .MuiChip-label": {
                                    px: 1,
                                  },
                                }}
                              />
                            )}
                        </Box>

                        {/* Employee Code */}
                        {suggestions.projection_fields.includes(
                          "employee_code",
                        ) &&
                          employee.employee_code && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.4,
                              }}
                            >
                              <HighlightText
                                text={employee.employee_code}
                                query={query}
                              />
                            </Typography>
                          )}
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </>
          )}
        </Paper>
      )}

      {/* ============================= */}
      {/* For Notification */}
      {/* ============================= */}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
});
